import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ProfileTypeId } from 'app/shared/enums/profile-type.enum';
import { DocumentStatusKey } from 'app/shared/helpers/document-status-map.helper';
import { FormDataPayload, convertPayloadToFormData, createHttpParams } from 'app/shared/helpers/utils';
import { config } from 'config';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface GetDocumentsPayload {
  user_id: number;
  order_id?: number;
  'sort_by[created_date]'?: string;
  'filters[tag_id]'?: number;
  'filters[search]'?: string;
}

export interface DocumentTag {
  tag_id: number;
  name: string;
}

export interface TagResponse {
  tags: Tag[];
  status: number;
}

export interface Tag {
  id: number;
  name: string;
  user_id?: number | null;
  status?: number;
  created_at?: string;
  updated_at?: string;
}


export interface Document {
  expiryDate?: string;
  document_id: number;
  title: string;
  url: string;
  userid: number;
  status: DocumentStatusKey;
  date: string;
  tags: DocumentTag[];
  access_level: number;
};

export interface GetDocumentsResponse {
  documents: Document[];
  status: number;
}
export interface DocumentSaveResponse {
  document: Document[];
  status: number;
}
export interface UploadDocumentPayload {
  document_name: string;
  tags: string;
  custom_tags: { name: string }[];
  user_id: number;
  order_id: number;
  document_id: number;
}
export interface GetTagsPayload {
  user_id?: number;
  order_id?: number;
  profile_type_id?: ProfileTypeId;
  get_defaults?: 0 | 1;
}
@Injectable({
  providedIn: 'root'
})
export class OrderDocumentsService {

  constructor(private http: HttpClient) { }

  getDocuments(payload: GetDocumentsPayload): Observable<GetDocumentsResponse> {
    const url = `${config.apiUrl1}documents`;
    const params = createHttpParams(payload);
    return this.http.get<GetDocumentsResponse>(url, { params }).pipe(
      catchError(err => {
        return throwError(err)
      })
    )
  }

  createDocument(payload: FormDataPayload): Observable<DocumentSaveResponse> {
    const url = `${config.apiUrl1}documents/create`;
    const formData = convertPayloadToFormData(payload);
    return this.http.post<DocumentSaveResponse>(url, formData).pipe(
      catchError(err => {
        return throwError(err)
      })
    )
  }

  updateDocument(payload: FormDataPayload): Observable<DocumentSaveResponse> {
    const formData = convertPayloadToFormData(payload);
    const url = `${config.apiUrl1}documents/update/${payload.document_id}`
    return this.http.post<DocumentSaveResponse>(url, formData).pipe(
      catchError(err => {
        return throwError(err)
      })
    )
  }

  deleteDocument(document: Document): Observable<void> {
    const url = `${config.apiUrl1}documents/delete/${document.document_id}`
    return this.http.post<void>(url, { user_id: document.userid }).pipe(
      catchError(err => {
        return throwError(err)
      })
    )
  }

  getTags(payload: GetTagsPayload): Observable<TagResponse> {
    const url = `${config.apiUrl1}tags`;
    const params = createHttpParams(payload);
    return this.http.get<TagResponse>(url, { params }).pipe(
      catchError(err => {
        return throwError(err)
      })
    )
  }

  getTagIdsByNames(tagNames: string[]): Observable<Map<string, number>> {
    const payload: GetTagsPayload = { get_defaults: 1 };
    return this.getTags(payload).pipe(
      map((response: TagResponse) => {
        const tagMap = new Map<string, number>();

        tagNames.forEach(name => {
          const match = response.tags.find(t => t.name.toLowerCase() === name.toLowerCase());
          if (match) {
            tagMap.set(name, match.id);
          }
        });

        return tagMap;
      }),
      catchError(err => throwError(() => err))
    );
  }

  generateQRCodeLink(payload: any) {
    const url = `${config.apiUrl1}keygen`
    return this.http.post<void>(url, payload, { responseType: 'text' as 'json' }).pipe(
      catchError(err => {
        return throwError(err)
      })
    )
  }
}
